// `avatar gitnexus` — Standalone command quản lý GitNexus integration (M10).
// 3 subcommands:
//   - install : Run full setup phase (install + setup + analyze + wiki + MCP)
//   - status  : Show index info (last commit, staleness, wiki age)
//   - analyze : Re-run analyze only (no wiki, không cost API)
//
// Cần chạy trong workspace dir — resolve qua resolveAvatarWorkspaceRootFromCwd
// để strict detect Avatar workspace (không phải folder random có .claude/).
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { Command } from "commander";
import { assertInAvatarWorkspaceOrExit } from "../lib/assert-in-avatar-workspace-or-hint-foreign.js";
import { pathExists, readJson } from "../lib/filesystem-helpers.js";
import { runGitnexusAnalyze } from "../lib/run-gitnexus-setup-and-analyze.js";
import { runGitnexusSetupPhase } from "../lib/run-gitnexus-setup-phase.js";
import { log } from "../lib/terminal-logger.js";

// Resolve Avatar workspace từ cwd (marker .claude/avatar.json). Exit 1 với hint
// đúng CLI nếu đứng nhầm nhà. Dùng guard chung — xem helper.
function ensureWorkspaceCwd(): string {
  return assertInAvatarWorkspaceOrExit(process.cwd());
}

// === install === v3: cài binary + skills global + MCP register. KHÔNG analyze
// (workspace root rỗng). Index per-repo qua 'avatar gitnexus analyze <name>'.
async function runGitnexusInstall(): Promise<void> {
  const workspacePath = ensureWorkspaceCwd();
  const result = await runGitnexusSetupPhase({ workspacePath, skipAnalyze: true });
  if (result.ok || result.installed) {
    log.success("GitNexus cài + MCP đăng ký xong.");
    log.dim("Index repo: avatar gitnexus analyze <name> | --all | (menu)");
  } else {
    log.warn(`Setup không complete: ${result.reason ?? "unknown"}`);
  }
}

// Đọc commit HEAD của repo từ filesystem (.git/HEAD + ref) — KHÔNG spawn git.
// HEAD có thể là: "ref: refs/heads/main" (đọc tiếp file ref) hoặc SHA trực tiếp
// (detached). Trả SHA, hoặc null nếu không đọc được (best-effort cho staleness).
async function resolveGitHead(repoPath: string): Promise<string | null> {
  try {
    const headPath = join(repoPath, ".git", "HEAD");
    const head = (await fs.readFile(headPath, "utf8")).trim();
    if (head.startsWith("ref:")) {
      const ref = head.slice(4).trim();
      try {
        return (await fs.readFile(join(repoPath, ".git", ref), "utf8")).trim();
      } catch {
        // Ref chưa có loose file (packed-refs) → fallback null, coi như không stale.
        return null;
      }
    }
    return head; // detached HEAD = SHA trực tiếp.
  } catch {
    return null;
  }
}

// === status === v3: per-repo. Hiện index status của TỪNG repo trong src/.
async function runGitnexusStatus(): Promise<void> {
  const workspacePath = ensureWorkspaceCwd();
  const { readReposManifest } = await import("../lib/workspace-repos-manifest-store.js");
  const repos = await readReposManifest(workspacePath);

  if (repos.length === 0) {
    log.dim("Chưa có repo nào trong workspace. Thêm bằng 'avatar add repo'.");
    return;
  }

  log.info(`GitNexus index status (${repos.length} repo):`);
  for (const r of repos) {
    const repoPath = join(workspacePath, "src", r.name);
    const metaPath = join(repoPath, ".gitnexus", "meta.json");
    if (!(await pathExists(metaPath))) {
      log.plain(`  ✗ ${r.name.padEnd(28)} chưa index — 'avatar gitnexus analyze ${r.name}'`);
      continue;
    }
    try {
      const meta = await readJson<{ lastCommit?: string; indexedAt?: string }>(metaPath);
      // Staleness: HEAD repo vs meta.lastCommit.
      // PERF: đọc HEAD trực tiếp từ filesystem (fast.resolveHead) thay vì spawn
      // `git rev-parse` cho TỪNG repo — 0 child process, status hiển thị nhanh
      // hơn nhiều với workspace nhiều repo.
      let stale = false;
      if (meta.lastCommit) {
        const head = await resolveGitHead(repoPath);
        if (head) stale = head !== meta.lastCommit;
      }
      const mark = stale ? "⚠ stale" : "✓ fresh";
      log.plain(`  ${mark} ${r.name.padEnd(28)} indexed ${meta.indexedAt?.slice(0, 10) ?? "?"}`);
    } catch {
      log.plain(`  ? ${r.name.padEnd(28)} (đọc meta.json fail)`);
    }
  }
  log.dim("\nStale → 'avatar gitnexus analyze <name>' để cập nhật.");
}

// === analyze === v3 per-repo: index 1 repo (name), tất cả (--all), hoặc menu chọn.
// Dùng để BỔ SUNG index cho repo chưa có, hoặc refresh repo stale.
async function runGitnexusAnalyzeCommand(
  name: string | undefined,
  opts: { all?: boolean },
): Promise<void> {
  const workspacePath = ensureWorkspaceCwd();
  const { readReposManifest } = await import("../lib/workspace-repos-manifest-store.js");
  const repos = await readReposManifest(workspacePath);

  if (repos.length === 0) {
    log.warn("Chưa có repo nào trong workspace. Thêm bằng 'avatar add repo'.");
    return;
  }

  // Xác định danh sách repo cần analyze.
  let targets: string[];
  if (opts.all) {
    targets = repos.map((r) => r.name);
  } else if (name) {
    if (!repos.some((r) => r.name === name)) {
      log.error(`Repo "${name}" không có trong workspace. Xem: avatar list repo`);
      process.exit(1);
    }
    targets = [name];
  } else {
    // Không tên + không --all → menu chọn.
    const { select } = await import("@inquirer/prompts");
    const choice = await select({
      message: "Index repo nào?",
      choices: [
        ...repos.map((r) => ({ name: r.name, value: r.name })),
        { name: "── Tất cả ──", value: "__all__" },
      ],
    });
    targets = choice === "__all__" ? repos.map((r) => r.name) : [choice];
  }

  // Analyze từng repo (per-repo, fail-soft: 1 repo fail không chặn repo khác).
  let ok = 0;
  for (const repoName of targets) {
    const repoPath = join(workspacePath, "src", repoName);
    try {
      await runGitnexusAnalyze(repoPath);
      ok += 1;
    } catch (err) {
      log.warn(`  ! ${repoName} fail: ${(err as Error).message}`);
    }
  }
  log.success(`Index xong ${ok}/${targets.length} repo. Xem: avatar gitnexus status`);
}

export function registerGitnexusCommand(program: Command): void {
  const gx = program.command("gitnexus").description("Quản lý GitNexus code intelligence (M10)");

  gx.command("install")
    .description("Cài + setup GitNexus cho workspace hiện tại")
    .action(async () => {
      await runGitnexusInstall();
    });

  gx.command("status")
    .description("Show index info + staleness warning")
    .action(async () => {
      await runGitnexusStatus();
    });

  gx.command("analyze [name]")
    .description("Index 1 repo trong src/ (tên), --all cho tất cả, không tên → menu")
    .option("--all", "Index mọi repo trong src/")
    .action(async (name: string | undefined, opts: { all?: boolean }) => {
      await runGitnexusAnalyzeCommand(name, opts);
    });
}
